import PropTypes from "prop-types";
import { Modal, Input, InputNumber, Switch } from "antd";
import BackArrowIcon from "../../../assets/left-arrow.svg";
import { useEffect, useRef } from "react";

import mapboxgl from "mapbox-gl";
import * as turf from "@turf/turf";
import { SearchBox } from "@mapbox/search-js-react";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

import { useDispatch, useSelector } from "react-redux";
import { updateUI, createRequest } from "../../../store/actions/location";
import getAddressfromLatLong from "../../../global/utility/getAddressfromLatLong";
import { IconButton } from "@mui/material";
import Fullscreen from "../../../assets/fullscreen.png";
import ExitFullscreen from "../../../assets/exit_fullscreen.png";
import { addDesignedButtons } from "../../Shipments/CreateShipments/map-utils";
import { mapboxToken, mapboxOwner, terrainId, satelliteId } from "../../../configuration";

function CreateNewLocation(props) {
  const { isBackButton, onBackClick } = props;
  const dispatch = useDispatch();
  const fullScreenContainerRef = useRef();
  const { isModalOpen, formFields, form } = useSelector((state) => ({
    isModalOpen: state?.location?.ui?.form?.type !== "",
    form: state?.location?.ui?.form,
    formFields: state?.location?.ui?.form?.fields,
  }));

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const geofenceCenterRef = useRef(null);

  const validateCoord = (coord) => {
    if (coord && Array.isArray(coord)) {
      if (!isNaN(coord[0]) && !isNaN(coord[1])) {
        if (
          coord[0] >= -180 &&
          coord[0] <= 180 &&
          coord[1] >= -90 &&
          coord[1] <= 90
        ) {
          return true;
        }
        return false;
      }
      return false;
    } else {
      return false;
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (mapRef.current) {
        mapRef.current.resize();
        AddCircleToMap();
        fitGeofenceBounds();
      }
      if (!document.fullscreenElement) {
        dispatch(
          updateUI({
            key: "form",
            state: {
              fields: {
                ...formFields,
                isFullScreen: false,
              },
            },
          })
        );
      } else {
        dispatch(
          updateUI({
            key: "form",
            state: {
              fields: {
                ...formFields,
                isFullScreen: true,
              },
            },
          })
        );
      }
    };

    const fullscreenEvents = [
      "fullscreenchange",
      "webkitfullscreenchange",
      "mozfullscreenchange",
      "MSFullscreenChange"
    ];

    fullscreenEvents.forEach(event => {
      document.addEventListener(event, handleFullscreenChange);
    });

    return () => {
      fullscreenEvents.forEach(event => {
        document.removeEventListener(event, handleFullscreenChange);
      });
    };
  }, [dispatch, formFields]);
  const formFieldsRef = useRef(formFields);
  useEffect(() => {
    formFieldsRef.current = formFields;
  }, [formFields]);

  const AddCircleToMap = () => {
    const latestFields = formFieldsRef.current;
    addOrUpdateGeofence(latestFields);
    addOrUpdateMarker(latestFields);
    fitGeofenceBounds(latestFields);
  };
  useEffect(() => {
    if (!isModalOpen || !formFields.isAdjustGeofanceOnMap) return;
    if (!mapContainerRef.current) return;

    mapboxgl.accessToken = mapboxToken;
    if (!mapboxgl.supported()) {
      alert("Your browser does not support Mapbox GL JS. Please try a different browser.");
      return;
    }

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/kiranmadu/cm8z3ig91009v01s4hx5y0nte",
      center: [formFields.longitude, formFields.latitude],
      zoom: 6,
    });

    mapRef.current.addControl(
      new mapboxgl.NavigationControl({ visualizePitch: true }),
      "bottom-right"
    );

    addDesignedButtons(mapRef.current, terrainId, satelliteId, mapboxOwner, AddCircleToMap);

    mapRef.current.on("load", () => {
      AddCircleToMap();

      let isDragging = false;
      mapRef.current.on("mousedown", "radius-handle-layer", (e) => {
        e.preventDefault();
        isDragging = true;
        mapRef.current.getCanvas().style.cursor = "grabbing";
        mapRef.current.dragPan.disable();
      });

      mapRef.current.on("mousemove", (e) => {
        if (!isDragging) return;
        const center = [
          formFieldsRef.current.longitude,
          formFieldsRef.current.latitude,
        ];
        const newRadius = Math.round(
          turf.distance(center, [e.lngLat.lng, e.lngLat.lat], { units: "meters" })
        );
        const newCircle = generateCircle(center, newRadius);
        const newHandle = getHandlePoint(center, newRadius);
        if (mapRef.current.getSource("geofence")) mapRef.current.getSource("geofence").setData(newCircle);
        if (mapRef.current.getSource("radius-handle")) mapRef.current.getSource("radius-handle").setData(newHandle);
      });

      mapRef.current.on("mouseup", (e) => {
        if (!isDragging) return;
        isDragging = false;
        mapRef.current.getCanvas().style.cursor = "";
        const center = [
          formFieldsRef.current.longitude,
          formFieldsRef.current.latitude,
        ];
        const newRadius = Math.round(
          turf.distance(center, [e.lngLat.lng, e.lngLat.lat], { units: "meters" })
        );
        dispatch(
          updateUI({
            key: "form",
            state: {
              fields: {
                ...formFieldsRef.current,
                radius: newRadius,
                isFullScreen: document.fullscreenElement !== null,
              },
            },
          })
        );
      });
    });
    mapRef.current.on('resize', () => {
      fitGeofenceBounds();
    });

    return () => {
      mapRef.current.remove();
      mapRef.current = null;
    };
  }, [isModalOpen, formFields.isAdjustGeofanceOnMap]);

  useEffect(() => {
    if (!mapRef.current || !mapRef.current.isStyleLoaded()) return;
    addOrUpdateGeofence();
    addOrUpdateMarker();
    fitGeofenceBounds();
  }, [formFields.latitude, formFields.longitude, formFields.radius]);

  function generateCircle(center, radius) {
    return turf.circle(center, radius, { steps: 80, units: "meters" });
  }
  function getHandlePoint(center, radius) {
    return turf.destination(center, radius, 90, { units: "meters" });
  }
  function addOrUpdateGeofence(fields = formFields) {
    const center = [fields.longitude, fields.latitude];
    const radius = fields.radius;
    const circle = generateCircle(center, radius);
    const handlePoint = getHandlePoint(center, radius);

    if (mapRef.current.getSource("geofence")) {
      mapRef.current.getSource("geofence").setData(circle);
    } else {
      mapRef.current.addSource("geofence", { type: "geojson", data: circle });
      mapRef.current.addLayer({
        id: "geofenceLayer",
        type: "fill",
        source: "geofence",
        paint: { "fill-color": "#088", "fill-opacity": 0.4 },
      });
    }

    if (mapRef.current.getSource("radius-handle")) {
      mapRef.current.getSource("radius-handle").setData(handlePoint);
    } else {
      mapRef.current.addSource("radius-handle", { type: "geojson", data: handlePoint });
      mapRef.current.addLayer({
        id: "radius-handle-layer",
        type: "line",
        source: "geofence",
        paint: { "line-dasharray": [3, 3], "line-color": "#0089EC", "line-width": 3 },
      });
    }
  }
  function addOrUpdateMarker(fields = formFields) {
    const center = [fields.longitude, fields.latitude];
    if (geofenceCenterRef.current) geofenceCenterRef.current.remove();
    geofenceCenterRef.current = new mapboxgl.Marker({ element: document.createElement("div") });
    geofenceCenterRef.current.getElement().innerHTML = `<div>
    <img src="/assets/geofence-center.svg" style="width:20.79px; height:20.79px" alt="geofence center point"/>
  </div>`;
    geofenceCenterRef.current.setLngLat(center).addTo(mapRef.current);
  }
  function fitGeofenceBounds(fields = formFieldsRef.current) {
    if (!mapRef.current) return;
    const { longitude, latitude, radius } = fields;
    if (
      typeof longitude !== "number" ||
      typeof latitude !== "number" ||
      typeof radius !== "number" ||
      isNaN(longitude) ||
      isNaN(latitude) ||
      isNaN(radius)
    ) {
      return;
    }

    const center = [longitude, latitude];
    const circle = turf.circle(center, radius / 1000, { units: "kilometers" });
    const boundingBox = turf.bbox(circle);

    mapRef.current.fitBounds(boundingBox, {
      padding: 100,
      duration: 1000,
    });
  }
  const handleFormField = async (field, value) => {
    if (field === "radius") {
      if (value === null) {
        dispatch(
          updateUI({
            key: "form",
            state: {
              fields: {
                ...form.fields,
                [field]: 500,
              },
            },
          })
        );
      } else {
        dispatch(
          updateUI({
            key: "form",
            state: {
              fields: {
                ...form.fields,
                [field]: value,
              },
            },
          })
        );
      }
      return;
    }

    if (field === "isAdjustGeofanceOnMap" && value === true) {
      if (
        formFields?.address &&
        formFields?.longitude &&
        formFields?.latitude
      ) {
        dispatch(
          updateUI({
            key: "form",
            state: {
              fields: {
                ...form.fields,
                [field]: value,
              },
            },
          })
        );
      } else {
        const address = '1 Verizon Way, Basking Ridge, New Jersey 07920, United States';
        dispatch(
          updateUI({
            key: "form",
            state: {
              fields: {
                ...form.fields,
                address,
                longitude: -74.53343516808366,
                latitude: 40.72960781069406,
                isFullScreen: false,
                [field]: value,
              },
            },
          })
        );
      }
      return;
    }
    dispatch(
      updateUI({
        key: "form",
        state: {
          fields: {
            ...form.fields,
            [field]: value,
          },
        },
      })
    );
  };

  const handleOkButton = () => { };
  const handleCancelButton = () => {
    if (isBackButton) {
      onBackClick();
    }
    dispatch(
      updateUI({
        key: "form",
        state: {
          ...form,
          type: "",
          single: {
            id: null,
            name: "",
          },
          fields: {
            pointName: "",
            address: "",
            latitude: "",
            longitude: "",
            radius: 500,
            isAdjustGeofanceOnMap: false,
            isFullScreen: false,
          },
        },
      })
    );
  };
  const handleSearchRetrieve = (res) => {
    if (
      res?.features &&
      res.features.length > 0 &&
      res.features[0]?.properties?.coordinates
    ) {
      const newFields = {
        ...formFields,
        address: res.features[0].properties.full_address,
        latitude: res.features[0].properties.coordinates.latitude,
        longitude: res.features[0].properties.coordinates.longitude,
        radius: formFields.radius || 500,
        isFullScreen: document.fullscreenElement !== null,
      };
      dispatch(
        updateUI({
          key: "form",
          state: {
            fields: newFields,
          },
        })
      );
      if (mapRef.current && mapRef.current.isStyleLoaded()) {
        const center = [newFields.longitude, newFields.latitude];
        mapRef.current.setCenter(center);
        addOrUpdateGeofence(newFields);
        addOrUpdateMarker(newFields);
        fitGeofenceBounds(newFields);
      }
    }
  };

  const handleSuggestError = (res) => {
    if (res.suggestions.length === 0) {
      console.log("handleSuggestError res", res);
    }
  };

  const handleAddButton = () => {
    let tempErrors = {};
    if (!formFields?.pointName) {
      tempErrors = {
        ...tempErrors,
        pointName: "Please enter a location name",
      };
    }

    if (!formFields?.address) {
      tempErrors = {
        ...tempErrors,
        address: "Please enter address",
      };
    }

    if (!formFields?.latitude) {
      tempErrors = {
        ...tempErrors,
        latitude: "Please enter lattitude",
      };
    }

    if (!formFields?.longitude) {
      tempErrors = {
        ...tempErrors,
        longitude: "Please enter lattitude",
      };
    }

    if (!formFields?.radius) {
      tempErrors = {
        ...tempErrors,
        radius: "radius can't be less than 1",
      };
    }

    if (Object.keys(tempErrors).length > 0) {
      dispatch(
        updateUI({
          key: "form",
          state: {
            fields: {
              ...form.fields,
              errors: tempErrors,
            },
          },
        })
      );
      return;
    }

    dispatch(createRequest(isBackButton));
  };
  const handleBackAndCancelButton = () => {
    onBackClick();
  };

  const handleFullScreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else fullScreenContainerRef.current.requestFullscreen();
  };

  return (
    <Modal
      id="clmap"
      className="d-flex flex-column justify-content-center align-items-center p-2 location-modal"
      title=""
      open={isModalOpen}
      onOk={handleOkButton}
      onCancel={handleCancelButton}
      width={{
        xs: "90%",
        sm: "80%",
        md: "90%",
        lg: "88%",
        xl: "85%",
        xxl: "80%",
      }}
      footer={() => {
        return [
          <button key="cancel" className="cancel" onClick={handleCancelButton}>
            Cancel
          </button>,
          <button key="submit" className="update" onClick={handleAddButton}>
            Add
          </button>,
        ];
      }}
    >
      {isBackButton && (
        <button
          onClick={handleBackAndCancelButton}
          className="deviceprofile-clone-back-button"
        >
          <img
            src={BackArrowIcon}
            alt="back button"
            width={"16px"}
            height={"16px"}
          />
        </button>
      )}
      <div
        className="font-face-vzw my-3"
        style={{
          fontWeight: 700,
          fontSize: "32px",
          color: "#000000",
          lineHeight: "36px",
        }}
      >
        Add location
      </div>
      <div className=" w-100 d-flex flex-row justify-content-start align-items-center">
        <div className="w-100 d-flex flex-column justify-content-center align-items-start position-relative">
          <span className="d-flex flex-row justify-content-start align-items-center device-profile-label">
            Location name
          </span>
          <div className="w-100 pl-2">
            <Input
              key={"[stateSelectorIndex]-point-name"}
              className={
                formFields?.errors?.pointName
                  ? `shipment-form-fields input-invalid-border-color`
                  : `shipment-form-fields`
              }
              value={formFields?.pointName || ""}
              onChange={(e) => handleFormField("pointName", e?.target?.value)}
            />
            {formFields?.errors?.pointName && (
              <span className="ats-error-message">
                {formFields?.errors?.pointName}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className=" w-100 d-flex flex-row justify-content-start align-items-center">
        <div className="w-100 d-flex flex-column justify-content-center align-items-start position-relative">
          <span className="d-flex flex-row justify-content-start align-items-center device-profile-label">
            Address
          </span>
          <div className="w-100 pl-2">
            <SearchBox
              className={`shipment-form-fields`}
              accessToken={mapboxToken}
              options={{
                language: "en",
                country: "US,CA,MX",
              }}
              value={formFields?.address || ""}
              onChange={(e) =>
                handleFormField("address", e?.target?.value || e)
              }
              onRetrieve={handleSearchRetrieve}
              onSuggest={handleSuggestError}
              id="address"
              name="address"
              theme={{
                cssText: `
                    .Input {
                        height: 42px;
                        font-size: 1rem;
                        font-weight: 400;
                        color: #212529;
                        font-family: system-ui;
                    }
                    .ClearBtn {
                        display: none !important;
                    }
                `,
                variables: {
                  unit: "14px",
                  padding: "0.5em",
                  border: formFields?.errors?.address
                    ? "1px solid rgba(186, 38, 38, 1)"
                    : "1px solid rgba(111, 113, 113, 1)",
                  borderRadius: "4px",
                  boxShadow: "none",
                },
              }}
            />
            {formFields?.errors?.address && (
              <span className="ats-error-message">
                {formFields?.errors?.address}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="py-2 w-100 d-flex flex-row justify-content-start align-items-center">
        <div className="w-33 d-flex flex-column justify-content-center align-items-start">
          <span className="d-flex flex-row justify-content-start align-items-center device-profile-label">
            Latitude
          </span>
          <div className="w-100 pe-2">
            <Input
              type="number"
              min={-90}
              max={90}
              readOnly={true}
              key={"[stateSelectorIndex]-location-type"}
              className={
                formFields?.errors?.latitude
                  ? `shipment-form-fields input-invalid-border-color`
                  : `shipment-form-fields`
              }
              value={formFields?.latitude}
              onChange={(e) => handleFormField("latitude", e?.target?.value)}
            />
            {formFields?.errors?.latitude && (
              <span className="ats-error-message">
                {formFields?.errors?.latitude}
              </span>
            )}
          </div>
        </div>
        <div className="w-33 d-flex flex-column justify-content-center align-items-start">
          <span className="d-flex flex-row justify-content-start align-items-center device-profile-label">
            Longitude
          </span>
          <div className="w-100 pe-2">
            <Input
              type="number"
              min={-90}
              max={90}
              readOnly={true}
              key={"[stateSelectorIndex]-location-type"}
              className={
                formFields?.errors?.longitude
                  ? `shipment-form-fields input-invalid-border-color`
                  : `shipment-form-fields`
              }
              value={formFields?.longitude}
              onChange={(e) => handleFormField("longitude", e?.target?.value)}
            />
            {formFields?.errors?.longitude && (
              <span className="ats-error-message">
                {formFields?.errors?.longitude}
              </span>
            )}
          </div>
        </div>
        <div className="w-33 d-flex flex-column justify-content-center align-items-start position-relative">
          <span className="d-flex flex-row justify-content-start align-items-center device-profile-label">
            Geofence radius(meters)
          </span>
          <div className="w-100 pl-2">
            <InputNumber
              key={"[stateSelectorIndex]-radius"}
              className={`shipment-form-fields`}
              style={{
                height: "44px",
              }}
              min={1}
              value={formFields?.radius}
              onKeyDown={(e) => {
                if (e.key === "Backspace" || e.key === "Delete") {
                  return;
                }
                const pattern = /[0-9]/;
                if (!pattern.test(e.key)) {
                  e.preventDefault();
                }
              }}
              onInput={(val) => {
                let tempErrors = {
                  ...form.fields.errors,
                };
                if (val.startsWith("0")) {
                  tempErrors = {
                    ...tempErrors,
                    radius: "radius can't be less than 1",
                  };
                } else {
                  tempErrors = {
                    ...tempErrors,
                    radius: "",
                  };
                }

                dispatch(
                  updateUI({
                    key: "form",
                    state: {
                      fields: {
                        ...form.fields,
                        errors: tempErrors,
                      },
                    },
                  })
                );
              }}
              onChange={(val) => handleFormField("radius", val)}
            />
          </div>
          {formFields?.errors?.radius && (
            <span className="ats-error-message">
              {formFields?.errors?.radius}
            </span>
          )}
        </div>
      </div>
      <div className="d-flex flex-row justify-content-start align-items-center">
        <span
          className="font-face-vzw"
          style={{
            fontWeight: 700,
            fontSize: "15px",
            color: "#000000",
            lineHeight: "20px",
            letterSpacing: "0.5px",
          }}
        >
          Adjust location on map
        </span>
        <div className="p-2">
          <Switch
            style={
              formFields?.isAdjustGeofanceOnMap
                ? {
                    backgroundColor: "#008331",
                  }
                : {
                    backgroundColor: "#d8dada",
                  }
            }
            checked={formFields?.isAdjustGeofanceOnMap}
            onChange={(checked) =>
              handleFormField("isAdjustGeofanceOnMap", checked)
            }
          />
        </div>
      </div>
      {formFields?.isAdjustGeofanceOnMap && (
        <div ref={fullScreenContainerRef}>
          <div
            className={
              formFields.isFullScreen
                ? "w-100 d-flex flex-column justify-content-center align-items-start map-container"
                : "py-2 w-100 d-flex flex-column justify-content-center align-items-start map-container"
            }
          >
            <IconButton
              style={{
                position: "absolute",
                top: "18px",
                right: "9px",
                zIndex: 1111,
                backgroundColor: "#fff",
                borderRadius: "8px",
                width: "32px",
                height: "32px",
              }}
              onClick={handleFullScreen}
            >
              <img
                alt="FullScreen button"
                src={formFields.isFullScreen ? ExitFullscreen : Fullscreen}
                height={"16px"}
                width={"16px"}
              />
            </IconButton>

            <div
              ref={mapContainerRef}
              style={
                formFields.isFullScreen
                  ? { height: "1000px", width: "100%" }
                  : { height: "388px", width: "100%" }
              }
            ></div>
          </div>
          <div
            className="mt-2"
            style={{
              fontWeight: 400,
              fontSize: "12px",
              color: "#6F7171",
              lineHeight: "16px",
              letterSpacing: "0px",
            }}
          >
            Click on the circumference and drag it to change the radius.
          </div>
        </div>
      )}
    </Modal>
  );
}

CreateNewLocation.propTypes = {
  isBackButton: PropTypes.bool,
  onBackClick: PropTypes.func,
};

export default CreateNewLocation;
